import type { components, paths } from "@securityjourney/svc-directory/dist/gen/directory";
import type {
  components as hubComponents,
  paths as hubPaths,
} from "@securityjourney/svc-directory/dist/gen/hubspot";
import type {
  components as csvComponents,
  paths as csvPaths,
} from "@securityjourney/svc-directory/dist/gen/csv_upload";

import { OpenApiFetchWrapper } from "./openApiFetchWrapper";

export type User = components["schemas"]["securityjourney.proto.directory.User"];
export type HubspotToken = hubComponents["schemas"]["securityjourney.proto.directory.HubspotToken"];
export type UserProfile = components["schemas"]["securityjourney.proto.directory.UserProfile"];
export type DeveloperProfile =
  components["schemas"]["securityjourney.proto.directory.DeveloperProfile"];
export type MemberList = components["schemas"]["securityjourney.proto.directory.Members"];
export type ErrStatus = components["schemas"]["connect.error"];
export type AttributeSearchFacet =
  components["schemas"]["securityjourney.proto.directory.AttributeSearchFacet"];
export type Tenant = components["schemas"]["securityjourney.proto.directory.User.Tenant"];
export type ArchiveRequest =
  components["schemas"]["securityjourney.proto.directory.ArchiveRequest"];
export type ArchiveResponse =
  components["schemas"]["securityjourney.proto.directory.ArchiveResponse"];
export type ArchiveFailure =
  components["schemas"]["securityjourney.proto.directory.ArchiveFailure"];

export type GetUsersOptions = {
  email?: string;
  givenName?: string;
  familyName?: string;
  includeArchived?: boolean;
  attributes?: AttributeSearchFacet;
};

type AllPaths = paths & hubPaths & csvPaths;

class DirectoryClient extends OpenApiFetchWrapper<AllPaths> {
  constructor() {
    super();
  }

  async getUser(tenant: string, directory_uuid: string) {
    return await this.get("/svc/directory/{tenant}/user/{directory_uuid}", {
      params: { path: { tenant, directory_uuid }, query: {} },
    });
  }

  async getUsersForTenant(tenant: string, options?: GetUsersOptions) {
    return await this.get("/svc/directory/{tenant}/users", {
      params: {
        path: { tenant },
        query: {
          email: options?.email,
          givenName: options?.givenName,
          familyName: options?.familyName,
          includeArchived: options?.includeArchived,
          attributes: options?.attributes,
        },
      },
    });
  }

  async createOrUpdateUser(user: User) {
    return await this.put("/svc/directory/user", { body: user });
  }

  // Useful when changing roles or adding a user to a new tenant.
  // Performs less authorization checks than createOrUpdateUser.
  async modifyUserMemberships(directory_uuid: string, tenants: Tenant[]) {
    return await this.put("/svc/directory/user", {
      body: { directoryUuid: directory_uuid, tenants },
    });
  }

  // returns the profile of the user who is currently authenticated
  async whoAmI() {
    return await this.get("/svc/directory/me");
  }

  // returns the hubspot ID token for the user who is currently authenticated
  async hubspotId() {
    return await this.get("/svc/directory/hubspot");
  }

  // updates the profile of the user who is currently authenticated
  // Note that the body object will patch the existing profile, so only send the fields to update.
  async updateSelf(body: UserProfile) {
    return await this.patch("/svc/directory/me", { body });
  }

  async uploadCsvToDirectory(tenant: string, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return await this.postFormData<
      csvComponents["schemas"]["securityjourney.proto.directory.CSVUploadResponse"]
    >(`/svc/directory/tenants/${tenant}/users/upload-csv`, formData);
  }

  async archiveUsers(tenant: string, directoryUuids: string[]) {
    return await this.post("/svc/directory/user/archive", {
      body: { tenant, directoryUuids },
    });
  }

  async unarchiveUsers(tenant: string, directoryUuids: string[]) {
    return await this.post("/svc/directory/user/unarchive", {
      body: { tenant, directoryUuids },
    });
  }

  // Deletes a tenant by name. Can only be done by SJ Admin.
  async deleteTenant(tenant: string) {
    return await this.delete("/svc/directory/tenant/{tenant}", {
      params: { path: { tenant } },
    });
  }
}

let directoryClientInstance: DirectoryClient | null = null;

export const getDirectoryClient = (): DirectoryClient => {
  if (!directoryClientInstance) {
    directoryClientInstance = new DirectoryClient();
  }
  return directoryClientInstance;
};
